<template>
  <th
    ref="table-header"
    class="m-table-header"
    :class="{
      'm-table-header_superheader': isSuperHeader,
      'm-table-header_subheader': isSubHeader,
      'm-table-header_sortable': sortable,
      'm-table-header_sortable-disabled': sortingDisabled,
      'm-table-header_left': alignLeft,
      'm-table-header_right': alignRight,
      'm-table-header_center': alignCenter,
      'm-table-header_bottom-header': isBottomHeader,
      'm-table-header_resizing': resizing,
      'm-table-header_with-right-border': hasRightBorder,
      'm-table-header_sticky-column': sticky,
      'm-table-header_column-sticked': isColumnSticked,
      'm-table-header_is-first': isFirst,
      'm-table-header_is-last': isLast,
    }"
    :rowspan="heightInCells"
    :colspan="widthInCells"
    :style="cssVars"
    :data-header-key="itemKey"
  >
    <div
      ref="columnStickyTrigger"
      class="m-table-header__sticky-trigger"
    />
    <div
      ref="header-content"
      class="m-table-header__content-wrapper"
      @click="$emit('click', [$event, itemKey])"
    >
      <div class="m-table-header__content">
        <MHelp
          v-if="headerHintPinVisible"
          placement="top"
          :popper-options="{
            modifiers: {
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport'
              }
            }
          }"
          :width="headerHintWidth"
          :show-on="headerHintShowOnProps"
          class="m-table-header__help"
          @click.native.stop="handleClickHeaderHint"
        >
          {{ headerHint }}
        </MHelp>
        <div class="m-table-header-content">
          <slot />
        </div>
        <MTableHeaderSorting
          v-if="sortable"
          :sort-direction="sortDirection"
          :disabled="sortingDisabled"
        />
        <div
          v-if="resizable && !isSuperHeader"
          class="m-table-header-resize-node"
          :class="{
            'm-table-header-resize-node_active': resizing,
            'm-table-header-resize-node__subheader': isSubHeader && hasRightBorder
          }"
          @mousedown="resizeActivate"
        >
          <div class="m-table-header-resize-node__indicator" />
        </div>
        <div
          v-if="isFirst && getBack"
          class="m-table-header-content-back-arrow"
        >
          <MIcon
            :icon="arrowBack"
            @click="getOnPreviousPage"
          />
        </div>
      </div>
    </div>
  </th>
</template>

<script>
  import getCssVarNameForColumn from 'components/Data/Tables/MTable/utils/getCssVarNameForColumn';
  import nextFrame from 'utils/nextFrame';
  import MTableHeaderSorting from 'components/Data/Tables/MTable/MTableHeaderSorting';
  import createObserver from 'utils/observers/createObserver';
  import MIcon from 'components/Design/MIcon.vue';
  import arrowBack from 'assets/icons/arrow-back.svg';
  import MHelp from 'components/Help/MHelp';
  import getMaxTextWidth from 'components/Data/Tables/MTable/utils/getMaxTextWidth';

  export default {
    name: 'MTableHeader',
    components: {MTableHeaderSorting, MIcon, MHelp},
    props: {
      itemKey: {
        type: String,
        default: ''
      },
      lastItemKey: { // should be defined when 2 level headers are used, is needed to pass resize event
        type: String,
        default: ''
      },
      align: {
        type: String,
        default: 'right'
      },
      headerWordBreak: { // should header title break title into multiple lines
        type: Boolean,
        default: false
      },
      headerHint: {
        type: String,
        default: ''
      },
      headerHintPinOnlyIcon: {
        type: Boolean,
        default: false
      },
      headerHintWidth: {
        type: String,
        default: '168px'
      },
      minWidth: {
        type: Number,
        default: 0
      },
      resizable: {
        type: Boolean,
        default: true
      },
      lazyResize: {
        type: Boolean,
        default: false
      },
      widthInCells: {
        type: Number,
        default: 0
      },
      heightInCells: {
        type: Number,
        default: 1
      },
      isSuperHeader: {
        type: Boolean,
        default: false
      },
      doubleHeight: {
        type: Boolean,
        default: false
      },
      hasRightBorder: {
        type: Boolean,
        default: false
      },
      sortable: Boolean,
      sortDirection: {
        type: String,
        default: 'none',
        choices: ['none', 'asc', 'desc']
      },
      tableParams: {
        type: Object,
        default: Object
      },

      static: Boolean,

      /*
       * object of sticky offsets
       * { columnKey: XXXpx, ...}
       */
      stickyOffset: {
        type: Object,
        default: Object
      },

      // is cell Sticky, enables position: sticky
      sticky: {
        type: Boolean,
        default: false
      },

      // map of columns that has sticky left neighbour, this is used to detect sticky state of left sticky column
      stickyNeighbourMap: {
        type: Object,
        default: Object
      },

      /*
       * key to find is this column sticky, with singleline headers is is itemKey,
       * with multiline tables it is last child.itemKey
       */
      stickyKey: {
        type: String,
        default: null
      },

      // key that triggers shadow, used for multiline table
      stickyShadowActiveKey: {
        type: String,
        default: null
      },

      /*
       * object with column and stickied flag
       * {columnKey: isSticky, .... }
       */
      stickedColumns: {
        type: Object,
        default: Object
      },

      // isFirst or isLast to add padding to header node
      isFirst: {
        type: Boolean
      },
      isLast: {
        type: Boolean
      },

      multilineHeader: {
        type: Boolean
      },
      headerRowIndex: {
        type: Number,
        default: 0
      },
      getBack: {
        type: Boolean,
        default: false
      },
      disableSortingPredicate: {
        type: Function,
        default: () => false
      },
      resizeIndicatorWidth: {
        type: Number,
        default: 4
      },
      dbClickResize: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        initialX: 0,
        width: 0,
        resizing: false,
        arrowBack,

        dbClickLock: 0,
        dbClickDelay: 500,
        dbClickResizeMultiplier: 1.1
      };
    },
    computed: {
      headerHintShowOnProps () {
        return this.headerHintPinOnlyIcon ? 'none' : 'click';
      },
      headerHintPinVisible () {
        return this.headerHint || this.headerHintPinOnlyIcon;
      },
      sortingDisabled () {
        return this.disableSortingPredicate(this.itemKey);
      },
      isColumnSticked () {

        /*
         * this strange logic is for multiline headers when shadow trigger is for last child header
         * but other params is for first child
         */
        const stickyKey = this.stickyShadowActiveKey || this.stickyKey;
        return this.sticky && this.stickedColumns[stickyKey];
      },
      cssVars () {
        const widthVar = `var(${getCssVarNameForColumn(this.itemKey, 'width')})`;
        return {
          '--current-cell-width': widthVar,
          '--sticky-left-offset': this.stickyLeftOffset
        };
      },
      stickyLeftOffset () {
        return this.stickyOffset[this.stickyKey];
      },
      stickyNeighbour () {
        return this.stickyNeighbourMap[this.stickyKey];
      },
      headerWidth () {
        const width = this.tableParams[this.itemKey] ? this.tableParams[this.itemKey].width : 0;
        if (width < this.minWidthWithDefault) {
          return this.minWidthWithDefault;
        }
        return width;
      },
      isSubHeader () {
        return this.multilineHeader && !this.isSuperHeader && this.heightInCells === 1;
      },
      computedAlign () {
        return this.isSuperHeader ? 'center' : this.align;
      },
      alignRight () {
        return this.computedAlign === 'right';
      },
      alignCenter () {
        return this.computedAlign === 'center';
      },
      alignLeft () {
        return this.computedAlign === 'left';
      },
      minWidthWithDefault () {
        return this.minWidth || 50;
      },
      isBottomHeader () {
        if (this.multilineHeader) {
          return this.heightInCells === 2 || this.headerRowIndex === 1;
        }
        return true;
      }
    },
    updated () {
      this.observeSticky();
    },
    destroyed () {
      if (this.stickyObserver) {
        this.stickyObserver.disconnect();
      }
    },
    methods: {
      handleClickHeaderHint () {
        this.$emit('handle-click-header-hint', this.itemKey);
      },
      observeSticky () {
        nextFrame(() => {
          if (!this.$refs.columnStickyTrigger) {
            return;
          }

          /*
           * throttle prevents from blinking borders when intersection observer meets border
           * may be this is caused by position sticky
           */
          const triggerSticky = (entries) => {
            this.$emit('triggerSticky', this.stickyNeighbour, !entries[0].isIntersecting);
          };

          if (this.stickyNeighbour) {
            if (this.stickyObserver) {
              this.stickyObserver.disconnect();
            }

            const {observer} = createObserver(
              'IntersectionObserver',
              this.$refs.columnStickyTrigger,
              triggerSticky,
              {
                root: this.$parent.$parent.$el,
                threshold: 1.0
              }
            );
            this.stickyObserver = observer;
          }
        });
      },
      getWidth () {
        if (this.static) {
          return this.minWidthWithDefault;
        }
        if (!this.resizable || this.$el.offsetWidth < this.minWidthWithDefault) {
          return this.minWidthWithDefault;
        }

        return this.$el.offsetWidth;
      },
      resizeActivate (e) {
        this.resizing = true;
        this.dbClickLock = this.dbClickLock + 1;

        // catch double clicks
        if (this.dbClickLock > 1) {
          this.resizeDBClick();
          return;
        }

        // first click
        setTimeout(() => {
          if (this.dbClickLock > 1) {
            // do this if dbClick has happened
            this.resizing = false;
          }
          this.dbClickLock = 0;
        }, this.dbClickDelay);


        this.initialX = e.clientX;
        this.width = this.headerWidth;

        if (this.lazyResize) {
          this.initiateResizeIndicator();
        }

        // add element class to prevent selecting text on page
        document.body.classList.add('body-resize-preventer');

        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
      },
      handleMouseMove (e) {
        const deltaX = e.clientX - this.initialX;
        if (this.lazyResize) {
          this.moveResizeIndicator(deltaX, e.clientX);
        } else {
          this.resizeColumn(deltaX, e.clientX);
        }
      },

      handleMouseUp (e) {
        if (!this.dbClickLock && this.lazyResize) {
          const deltaX = e.clientX - this.initialX;
          this.resizeColumn(deltaX, e.clientX);
          this.hideResizeIndicator();
        }

        this.resizing = false;

        document.body.classList.remove('body-resize-preventer');

        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
      },

      resizeColumn (delta, current) {
        if (this.width + delta > this.minWidthWithDefault) {
          this.initialX = current;
          this.width += delta;

          // send new width to MTable, no component resizing is made by component, all this work is done by MTable
          this.$emit('column-resize', {key: this.itemKey, value: this.width});
        } else {
          this.$emit('column-resize', {key: this.itemKey, value: this.minWidthWithDefault});
        }
      },

      getColumnOptimalWidth () {
        function getId (key) {
          const idx = key.split('-')[1];

          if (idx && idx.length && !isNaN(idx)) {
            return Number(idx);
          }
        }

        const columnIdx = getId(this.$vnode.key);

        if (columnIdx === null) {
          return;
        }

        const tBodyEl = this.$el.parentElement.parentElement.nextElementSibling;
        if (tBodyEl.tagName !== 'TBODY') {
          return;
        }

        const texts = [];
        let fontSize = null;

        for (let i = 0; i < tBodyEl.children.length - 1; i++) {
          const row = tBodyEl.children[i];
          if (!row.className.includes('spacer')) {
            const cell = row.children[columnIdx];
            const text = cell.textContent;

            if (!fontSize) {
              fontSize = window.getComputedStyle(cell, null).getPropertyValue('font-size');
            }

            texts.push(text);
          }
        }

        return getMaxTextWidth(texts, fontSize);
      },

      resizeDBClick () {
        let width = this.getColumnOptimalWidth();
        if (width) {
          width = width * this.dbClickResizeMultiplier;
        } else {
          width = this.minWidth;
        }
        this.$emit('column-resize', {key: this.itemKey, value: width >= this.minWidth ? width : this.minWidth});
      },

      getOnPreviousPage () {
        this.$emit('getBack');
      },

      initiateResizeIndicator () {
        const resizeIndicator = document.getElementById('m-table-header-lazy-resize-indicator');

        let tableElement = document.getElementsByClassName('m-table__body');
        if (tableElement.length) {
          tableElement = tableElement[0];
          const tableHeight = tableElement.getBoundingClientRect().height;

          resizeIndicator.style.height = `${tableHeight}px`;
        }

        resizeIndicator.style.width = `${this.resizeIndicatorWidth}px`;
      },

      hideResizeIndicator () {
        const resizeIndicator = document.getElementById('m-table-header-lazy-resize-indicator');
        resizeIndicator.style.display = 'none';
      },

      moveResizeIndicator (delta, current) {
        if (this.width + delta > this.minWidthWithDefault) {
          const resizeIndicator = document.getElementById('m-table-header-lazy-resize-indicator');

          const parentLeftOffset = resizeIndicator.parentElement.getBoundingClientRect().left;
          const indicatorLeftOffset = current - parentLeftOffset - this.resizeIndicatorWidth / 2;

          resizeIndicator.style.left = `${indicatorLeftOffset}px`;

          resizeIndicator.style.display = 'block';
        }
      }
    }
  };
</script>

<style>
  .m-table-header {
    --bottom-shadow-offset: 0;

    font-weight: var(--medium-font-weight);

    vertical-align: bottom;

    position: sticky;
    top: 0;
    z-index: 16;

    height: 100%;

    background: var(--background-color);
  }

  .m-table-header__help {
    margin: 0 4px;
  }

  .m-table-header__content {
    color: var(--text-main-color);

    display: flex;

    flex-grow: 0;
    flex-shrink: 0;

    box-sizing: border-box;
    width: 100%;

    padding: 12px 8px;
  }

  .m-table-header_is-first .m-table-header__content {
    padding-left: 14px;
  }

  .m-table-header_is-last .m-table-header__content {
    padding-right: 14px;
  }

  .m-table-header_sortable {
    cursor: pointer;
    user-select: none;
  }

  .m-table-header_sortable-disabled {
    cursor: unset;
  }

  .m-table__body th.m-table-header_superheader {
    height: 34px;
  }

  .m-table-header_superheader .m-table-header__content {
    padding-bottom: 4px;
  }

  .m-table-header_subheader .m-table-header__content {
    padding-top: 4px;
  }

  .m-table-header_subheader {
    top: 34px;
  }

  .m-table-header__content-wrapper {
    position: relative;
    display: flex;

    flex-grow: 0;
    flex-shrink: 0;
    align-items: flex-end;

    width: var(--current-cell-width);
    height: var(--cell-height);
  }

  .m-table-header_sticky-column {
    left: var(--sticky-left-offset);
    z-index: 20;
  }

  .m-table-header_sticky-column::after {
    content: '';

    position: absolute;
    top: 0;
    right: -30px;

    width: 30px;
    height: 100%;

    box-shadow: inset 31px 0 18px -20px var(--sticky-cell-vertical-shadow-color);

    opacity: 0;

    transition: opacity 100ms;

    pointer-events: none;
  }

  .m-table-header__sticky-trigger {
    position: absolute;
    bottom: 0;
    left: calc(var(--sticky-left-offset) * -1);
  }

  .m-table-header_with-right-border {
    box-sizing: border-box;
    border-right: 1px solid var(--border-color);
  }

  .m-table-header_bottom-header .m-table-header__content-wrapper {
    border-bottom: 1px solid var(--border-color);
  }

  .m-table_sticky .m-table-header_bottom-header .m-table-header__content-wrapper {
    border-bottom-color: var(--background-color);
  }

  .m-table_sticky .m-table-header_bottom-header .m-table-header__content-wrapper::after {
    content: '';

    position: absolute;
    bottom: -40px;
    left: 0;
    z-index: -5;

    width: 100%;
    height: 40px;

    box-shadow: inset 0 11px 12px -5px var(--sticky-header-cell-shadow-color);

    opacity: 0;

    transition: opacity 100ms;

    pointer-events: none;
  }

  .m-table-header_column-sticked::after,
  .m-table_sticky .m-table-header_bottom-header .m-table-header__content-wrapper::after {
    opacity: 1;
  }

  .m-table-header_column-sticked.m-table-header_with-right-border {
    border-right-color: var(--background-color);
  }

  .m-table-header_with-right-border.m-table-header_bottom-header .m-table-header__content-wrapper::after {
    /* deal with border */
    width: calc(100% + 1px);
  }

  .m-table-header_left .m-table-header__content,
  .m-table-header_right .m-table-header__content,
  .m-table-header_center .m-table-header__content {
    align-items: center;
  }

  .m-table-header_left .m-table-header__content {
    text-align: left;
    justify-content: start;
  }

  .m-table-header_right .m-table-header__content {
    text-align: right;
    justify-content: end;
  }

  .m-table-header_center .m-table-header__content {
    text-align: center;
    justify-content: center;
  }

  .m-table-header_sortable.m-table-header_right .m-table-header__content {
    padding-right: 0;
  }

  .m-table-header_is-last.m-table-header_sortable.m-table-header_right .m-table-header__content,
  .m-table-header_sortable.m-table-header_right.m-table-header_with-right-border .m-table-header__content {
    padding-right: 8px;
  }

  .m-table-header-content {
    align-self: end;
    min-width: 0;
  }

  .m-table-header_resizing {
    cursor: ew-resize;
    pointer-events: none;
    user-select: none;
  }

  .m-table-header-resize-node {
    cursor: ew-resize;

    position: absolute;
    display: none;
    top: 0;
    right: 0;

    height: 100%;

    padding-left: 3px;
  }

  .m-table-header-resize-node_active,
  .m-table-header:hover .m-table-header-resize-node {
    background: var(--action-secondary-color);
  }

  .m-table-header:hover .m-table-header-resize-node__indicator {
    background-color: var(--resize-node-color);
  }

  .m-table-header-resize-node__subheader {
    height: calc(34px + 100%);
    margin-top: -35px;
  }

  .m-table-header-content-back-arrow {
    color: var(--action-color);

    cursor: pointer;

    width: 16px;
    height: 16px;
  }

  .m-table-header-content-back-arrow:hover {
    color: var(--action-hover-color);
    cursor: pointer;
  }

</style>
